import { useState, useEffect } from "react";
import { toast } from "sonner";
import client, { imgUrl, shootCover } from "@/lib/glossyApi";
import {
  RefreshCw, Save, Sparkles, X, Image as ImageIcon, PenLine, AlertTriangle, Send, Eye, Images, RotateCcw, Camera,
} from "lucide-react";
import SavedImagePicker from "./SavedImagePicker";

const HOUSE_PORTRAIT_STYLE = "Fresh, edgy cover-star portrait — bold colour backdrop, modern lighting with attitude, relaxed confident pose, full of character. Cool British indie-magazine energy, never corporate, never stiff";
const HOUSE_COVER_STYLE = "Vogue/GQ editorial fashion cover — clean elegant studio lighting, rich natural colour, high-end fashion styling, confident cover-star presence. Real fashion photography: NO smoke, NO haze, NO fog, NO coloured gel lighting, NO theatrical effects.";

/**
 * Cover Member panel — the member's permanent portrait + written profile, in ONE unified card.
 *
 * LOCKED template (do NOT alter):
 *   - S/ME masthead top-left + "The Online SME Magazine" tagline top-right (stamped by share_kit.stamp_masthead)
 *   - Portrait fills the 2:3 frame
 *   - Location + Owner name + Business name stack, bottom-left (rendered here via CSS,
 *     matches the public /cover-members grid card exactly)
 *
 * The Cover Member image doubles as the social media tile (feed + story).
 * Every generated shot is auto-saved to the AI Images Saved gallery in the Master section.
 * Face Lock + 3+ Master Photos are compulsory before Generate will run (enforced on the backend too).
 * "Publish now" pushes THIS member live — Cover Member card + individual /member page + homepage rotation.
 */
export default function CoverMemberSection({ member, setMember, onChange }) {
  const m = member;
  const feature = (m.package && m.package.feature) || {};
  const [title, setTitle] = useState(feature.title || "");
  const [dek, setDek] = useState(feature.dek || "");
  const [body, setBody] = useState(feature.body || "");
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [notes, setNotes] = useState(m.feature_client_notes || "");

  const applyNotes = async () => {
    if (notes.trim().length < 10) { toast.error("Paste the client's notes first."); return; }
    if (!window.confirm("Apply their notes now? The desk makes ONLY the changes they asked for — every other sentence stays word-for-word. This replaces the current draft.")) return;
    setBusy("notes");
    try {
      const r = await client.post(`/admin/members/${m.id}/feature/apply-notes`, { notes });
      const f = r.data.feature || {};
      setTitle(f.title || ""); setDek(f.dek || ""); setBody(f.body || ""); setDirty(false);
      setMember({ ...m, package: { ...(m.package || {}), feature: f }, feature_client_notes: notes });
      const n = (r.data.notes_summary || []).length;
      toast.success(n ? `Done — ${n} change${n === 1 ? "" : "s"} applied, everything else untouched.` : "Their notes were applied.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't apply the notes."); }
    setBusy("");
  };
  const [portraitDirection, setPortraitDirection] = useState(HOUSE_PORTRAIT_STYLE);
  const [coverDirection, setCoverDirection] = useState(HOUSE_COVER_STYLE);

  useEffect(() => {
    if (dirty) return;
    const f = (m.package && m.package.feature) || {};
    setTitle(f.title || "");
    setDek(f.dek || "");
    setBody(f.body || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.id, feature.title, feature.dek, feature.body]);

  const swapFromSaved = async (path) => {
    setBusy("swap");
    try {
      const r = await client.post(`/admin/members/${m.id}/shot-gallery/assign`, { image: path, target: "directory_image" });
      setMember({ ...m, ...(r.data.member || {}) });
      setShowPicker(false);
      toast.success("Portrait swapped — S/ME masthead stamped on. No credits spent.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't swap."); }
    setBusy("");
  };

  const photoCount = (m.photos || []).length;
  const facelockOk = !!m.likeness_ref && photoCount >= 3;

  const generateImage = async () => {
    if (!facelockOk) { toast.error("Set Face Lock in the Master section first."); return; }
    setBusy("image");
    try {
      const r = await shootCover(m.id, {
        style: portraitDirection.trim() || HOUSE_PORTRAIT_STYLE,
        preset: "directory",
        target: "directory_image",
        include_sme_logo: true,
      });
      setMember({ ...m, directory_image: r.data.image, shot_gallery: [...(m.shot_gallery || []), r.data.shot].filter(Boolean) });
      toast.success("Cover Member shot generated — S/ME masthead stamped on.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed."); }
    setBusy("");
  };

  const generateEditorialCover = async () => {
    if (!facelockOk) { toast.error("Set Face Lock in the Master section first."); return; }
    setBusy("ed-cover");
    try {
      const r = await shootCover(m.id, {
        style: coverDirection.trim() || HOUSE_COVER_STYLE,
        preset: "editorial",
        target: "cover_image",
      });
      setMember({ ...m, cover_image: r.data.image, shot_gallery: [...(m.shot_gallery || []), r.data.shot].filter(Boolean) });
      toast.success("Editorial cover shot — this is the hero image everyone sees.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Cover shoot failed."); }
    setBusy("");
  };

  const clearImage = async () => {
    if (!m.directory_image) return;
    if (!window.confirm("Remove the current Cover Member portrait? The shot stays in the AI Images Saved gallery so you can pick it again later.")) return;
    setBusy("clear-image");
    try {
      await client.post(`/admin/members/${m.id}/clear-cover-image`);
      setMember({ ...m, directory_image: null });
      toast.success("Cover Member portrait removed.");
      onChange && onChange();
    } catch { toast.error("Couldn't remove."); }
    setBusy("");
  };

  const writeProfile = async () => {
    if (!window.confirm("Draft a fresh Cover Member profile using every field in the Master section? This spends AI credits and overwrites the current draft below.")) return;
    setBusy("write");
    try {
      const r = await client.post(`/admin/members/${m.id}/write-cover-profile`);
      setTitle(r.data.title || "");
      setDek(r.data.dek || "");
      setBody(r.data.body || "");
      setMember({ ...m, package: { ...(m.package || {}), feature: r.data } });
      setDirty(false);
      toast.success("Profile drafted from the Master section. Edit anything, then Save.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Writing failed."); }
    setBusy("");
  };

  const saveProfile = async () => {
    setBusy("save-profile");
    try {
      const r = await client.patch(`/admin/members/${m.id}/cover-profile`, { title, dek, body });
      setMember({ ...m, package: { ...(m.package || {}), feature: r.data } });
      setDirty(false);
      toast.success("Cover Member profile saved.");
      onChange && onChange();
    } catch { toast.error("Save failed."); }
    setBusy("");
  };

  const publishNow = async () => {
    if (!m.directory_image) { toast.error("Generate the Cover Member portrait first."); return; }
    if (!title || !body) { toast.error("Write (or draft with AI) the profile before publishing."); return; }
    if (!window.confirm("Publish this member? Their Cover Member card, individual profile page and homepage rotation slot go live now.")) return;
    setBusy("publish");
    try {
      await client.post(`/admin/members/${m.id}/publish`);
      setMember({ ...m, status: "published" });
      toast.success("Published live. Their Cover Member card is now on the public site.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed."); }
    setBusy("");
  };

  const track = (setter, key) => (e) => {
    setter(e.target.value);
    setDirty(true);
    // mirror into the parent so the Master-level Save also carries these edits
    setMember((prev) => ({ ...prev, package: { ...(prev.package || {}), feature: { ...((prev.package || {}).feature || {}), [key]: e.target.value } } }));
  };
  const isLive = m.status === "published";

  return (
    <section id="section-cover-member" data-testid="cover-member-section" className="space-y-4 pt-4 scroll-mt-24">
      {/* Section banner — same treatment as Master section */}
      <div className="bg-ink text-cream px-5 py-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 grain relative">
        <span className="overline text-gloss-gold relative z-10">Cover Member · their permanent portrait + written profile</span>
      </div>

      {!facelockOk && (
        <div className="border border-gloss-gold/50 bg-gloss-gold/10 p-4 flex items-start gap-3" data-testid="cover-facelock-warn">
          <AlertTriangle size={18} className="text-gloss-gold shrink-0 mt-0.5" />
          <div>
            <p className="font-sans text-sm text-ink"><b>AI generation is locked.</b> Go back to the Master section and add at least 3 photos, then Face-Lock one of them.</p>
            <p className="font-mono text-[11px] text-ink/50 mt-1">Currently: {photoCount}/3 photos · Face lock {m.likeness_ref ? "SET" : "NOT SET"}.</p>
          </div>
        </div>
      )}

      {/* ============ ONE UNIFIED CARD ============ */}
      <div className="border border-ink/15 bg-white" data-testid="cover-member-card">
        {/* Top row: portrait preview + image controls */}
        <div className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start border-b border-ink/10">
          <div>
            <p className="overline text-ink/60 mb-2">The portrait</p>
            <div className="relative aspect-[2/3] bg-ink border border-ink/15 overflow-hidden" data-testid="cover-preview">
              {m.directory_image ? (
                <>
                  <img src={imgUrl({ cover_image: m.directory_image })} alt="Cover Member" className="w-full h-full object-cover object-top" data-testid="cover-preview-img" />
                  <div className="absolute inset-0 pointer-events-none flex flex-col justify-end p-4 sm:p-5" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.85) 100%)" }}>
                    {m.borough && <p className="overline text-[10px] tracking-widest text-gloss-gold mb-1.5" data-testid="cover-preview-location">{m.borough.toUpperCase()}</p>}
                    {m.owner_name && <p className="font-serif text-cream text-2xl leading-tight" data-testid="cover-preview-owner">{m.owner_name}</p>}
                    {m.business_name && <p className="font-sans text-cream/75 text-sm mt-0.5" data-testid="cover-preview-business">{m.business_name}</p>}
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-cream/40">
                  <ImageIcon size={32} />
                  <p className="overline">no portrait yet</p>
                </div>
              )}
              {busy === "image" && (
                <div className="absolute inset-0 bg-ink/80 flex flex-col items-center justify-center gap-3 text-cream" data-testid="cover-generating">
                  <RefreshCw size={22} className="animate-spin" />
                  <p className="overline">In the darkroom…</p>
                  <p className="font-mono text-[10px] text-cream/60">Usually 60–90 seconds</p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <p className="overline text-ink/60">Controls</p>
            <p className="font-sans text-sm text-ink/60 leading-relaxed">Generated in the S/ME house style — anchored on the Face-Locked photo. The S/ME masthead is stamped on afterwards (never AI-drawn). Location, owner and business name are added by the template layer.</p>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="overline text-[10px] text-ink/60">Shot direction — stance, position, mood (yours to change)</label>
                {portraitDirection !== HOUSE_PORTRAIT_STYLE && (
                  <button onClick={() => setPortraitDirection(HOUSE_PORTRAIT_STYLE)} className="overline text-[9px] text-ink/50 flex items-center gap-1 hover:text-ink transition-colors" data-testid="portrait-direction-reset">
                    <RotateCcw size={10} /> House style
                  </button>
                )}
              </div>
              <textarea value={portraitDirection} onChange={(e) => setPortraitDirection(e.target.value)} rows={3}
                className="w-full border border-ink/25 p-3 font-sans text-sm focus:outline-none focus:border-gloss-gold resize-none"
                placeholder={HOUSE_PORTRAIT_STYLE}
                data-testid="portrait-direction" />
            </div>
            <button onClick={generateImage} disabled={!facelockOk || !!busy}
              className="w-full bg-gloss-gold text-ink px-4 py-3 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="cover-generate-btn">
              {busy === "image" ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />} {m.directory_image ? "Generate a new portrait (credits)" : "Generate Cover Member portrait (credits)"}
            </button>
            <button onClick={() => setShowPicker(!showPicker)} disabled={!!busy}
              className="w-full border border-ink px-4 py-2.5 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="cover-swap-btn">
              <Images size={14} /> {showPicker ? "Close picker" : "Swap from saved (free)"}
            </button>
            {showPicker && (
              <SavedImagePicker member={m} current={m.directory_image} tid="cover-swap" busy={busy}
                note="Pick any saved image as the Cover Member portrait — the S/ME masthead is stamped on automatically. Free, no credits."
                onPick={swapFromSaved} />
            )}
            {m.directory_image && (
              <button onClick={clearImage} disabled={!!busy}
                className="w-full border border-ink/30 text-ink/60 px-4 py-2.5 overline flex items-center justify-center gap-2 hover:border-ink hover:text-ink transition-colors disabled:opacity-60" data-testid="cover-clear-btn">
                <X size={14} /> Remove current portrait
              </button>
            )}
            <div className="border border-ink/15 p-3" data-testid="hero-framing-row">
              <p className="overline text-[10px] text-ink/60 mb-2">Head in shot — framing on their /member page (this member only)</p>
              <select value={m.hero_fit || "top"}
                onChange={async (e) => { const v = e.target.value; try { await client.patch(`/admin/members/${m.id}`, { hero_fit: v }); setMember({ ...m, hero_fit: v }); toast.success("Framing saved — only this member's profile page changes."); } catch { toast.error("Couldn't save."); } }}
                className="w-full border border-ink/30 bg-transparent px-2 py-2 font-sans text-xs focus:outline-none" data-testid="hero-framing-select">
                <option value="top">Top of photo (house default)</option>
                <option value="center">Centre — face in the middle of the photo</option>
                <option value="low">Lower — face near the bottom of the photo</option>
              </select>
            </div>
            <div className="border border-ink/10 bg-secondary/50 p-3 text-sm font-sans text-ink/65">
              <p className="font-mono text-[11px] text-ink/55"><b className="text-ink">Every shot is saved.</b> Every AI portrait lands in <b>AI Images Saved</b> in the Master section. Reuse or delete from there.</p>
            </div>
            <div className="border border-ink/10 bg-secondary/50 p-3 text-sm font-sans text-ink/65">
              <p className="font-mono text-[11px] text-ink/55"><b className="text-ink">Doubles as their social tile.</b> The final S/ME-templated portrait is the same image used on their Instagram feed and Story.</p>
            </div>
          </div>
        </div>

        {/* ============ Editorial Cover — the Cover Star hero image ============ */}
        <div className="p-5 sm:p-6 border-b border-ink/10" data-testid="editorial-cover-block">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div>
              <p className="overline text-ink/60 mb-2">The Editorial cover — the Cover Star hero everyone sees</p>
              <div className="relative aspect-[2/3] bg-ink border border-ink/15 overflow-hidden" data-testid="editorial-cover-preview">
                {m.cover_image ? (
                  <img src={imgUrl({ cover_image: m.cover_image })} alt="Editorial cover" className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-cream/40">
                    <Camera size={32} />
                    <p className="overline">no editorial cover yet</p>
                  </div>
                )}
                {busy === "ed-cover" && (
                  <div className="absolute inset-0 bg-ink/80 flex flex-col items-center justify-center gap-3 text-cream">
                    <RefreshCw size={22} className="animate-spin" />
                    <p className="overline">On the cover set…</p>
                    <p className="font-mono text-[10px] text-cream/60">Usually 60–90 seconds</p>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <p className="font-sans text-sm text-ink/60 leading-relaxed">This is the big magazine-cover image — used as the homepage hero when they're the Cover Star. Direct the shoot below: stance, position, wardrobe, mood. Face-Locked to their real photos.</p>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="overline text-[10px] text-ink/60">Cover direction — yours to change</label>
                  {coverDirection !== HOUSE_COVER_STYLE && (
                    <button onClick={() => setCoverDirection(HOUSE_COVER_STYLE)} className="overline text-[9px] text-ink/50 flex items-center gap-1 hover:text-ink transition-colors" data-testid="cover-direction-reset">
                      <RotateCcw size={10} /> House style
                    </button>
                  )}
                </div>
                <textarea value={coverDirection} onChange={(e) => setCoverDirection(e.target.value)} rows={3}
                  className="w-full border border-ink/25 p-3 font-sans text-sm focus:outline-none focus:border-gloss-gold resize-none"
                  placeholder={HOUSE_COVER_STYLE}
                  data-testid="editorial-cover-direction" />
              </div>
              <button onClick={generateEditorialCover} disabled={!facelockOk || !!busy}
                className="w-full bg-ink text-cream px-4 py-3 overline flex items-center justify-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="editorial-cover-generate-btn">
                {busy === "ed-cover" ? <RefreshCw size={14} className="animate-spin" /> : <Camera size={14} />} {m.cover_image ? "Shoot a new Editorial cover (credits)" : "Shoot the Editorial cover (credits)"}
              </button>
            </div>
          </div>
        </div>

        {/* Bottom: written profile — same card, no gap */}
        <div className="p-5 sm:p-6 space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="overline text-ink/60">The written profile</p>
              <p className="font-sans text-sm text-ink/60 mt-1">Appears on their Cover Members card + individual page when you Publish. Write it yourself or draft with AI and edit — the AI uses <b>everything</b> in the Master section (Company File, Key Facts, reference documents, business basics, socials, opening hours). Every published piece also shows a &quot;how to contact / follow / visit them&quot; block automatically (their website, phone, email, address, socials — pulled from the Master).</p>
            </div>
            <button onClick={writeProfile} disabled={!!busy}
              className="bg-ink text-cream px-4 py-2.5 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60 shrink-0" data-testid="cover-write-ai-btn">
              {busy === "write" ? <RefreshCw size={14} className="animate-spin" /> : <PenLine size={14} />} Draft with AI
            </button>
          </div>

          <div>
            <label className="overline text-[10px] text-ink/60 block mb-1">Headline</label>
            <input value={title} onChange={track(setTitle, "title")} placeholder="A punchy profile headline"
              className="w-full border border-ink/30 px-4 py-2.5 font-serif text-lg focus:outline-none focus:border-ink" data-testid="cover-profile-title" />
          </div>
          <div>
            <label className="overline text-[10px] text-ink/60 block mb-1">Standfirst (one-line hook)</label>
            <input value={dek} onChange={track(setDek, "dek")} placeholder="A one-sentence standfirst that sells the read"
              className="w-full border border-ink/30 px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-ink" data-testid="cover-profile-dek" />
          </div>
          <div>
            <label className="overline text-[10px] text-ink/60 block mb-1">Body (Markdown supported — ## for subheads, &gt; for pull-quotes)</label>
            <textarea value={body} onChange={track(setBody, "body")} rows={14} placeholder="The full profile in Markdown — 500–700 words, editorial voice, scene-setting, personality, craft, ambition."
              className="w-full border border-ink/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-ink leading-relaxed" data-testid="cover-profile-body" />
            <div className="mt-4 border border-gloss-gold/50 bg-gloss-gold/5 p-3" data-testid="cover-profile-client-notes-panel">
              <label className="overline text-[10px] text-ink/60 block mb-1">Client notes — paste their feedback email; Apply makes ONLY those changes, the rest stays word-for-word</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
                placeholder={'e.g. "Please use my professional name throughout, remove the line about…, and update my qualification to…"'}
                className="w-full border border-ink/30 px-4 py-3 font-sans text-sm focus:outline-none focus:border-ink leading-relaxed" data-testid="cover-profile-client-notes" />
              <button onClick={applyNotes} disabled={!!busy || !body}
                className="mt-2 overline text-[10px] border border-gloss-gold bg-gloss-gold/20 px-3 py-2 flex items-center gap-1.5 hover:bg-gloss-gold transition-colors disabled:opacity-50"
                data-testid="cover-profile-apply-notes-btn">
                {busy === "notes" ? "Applying their notes…" : "Apply their notes to the profile"}
              </button>
              {m.feature_notes_applied_at && <p className="font-mono text-[10px] text-ink/40 mt-2" data-testid="cover-profile-notes-applied-at">Notes last applied {new Date(m.feature_notes_applied_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} — the notes stay saved here for reference.</p>}
            </div>
          </div>

          {/* Save row + section-level Publish Now */}
          <div className="mt-4 pt-4 border-t border-ink/10 flex items-center justify-between gap-3 flex-wrap">
            <p className={`font-mono text-[11px] ${dirty ? "text-gloss-gold" : "text-ink/40"}`} data-testid="cover-profile-status">
              {dirty ? "Unsaved changes" : (title || body ? "Saved" : "Empty — start writing or draft with AI")}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={saveProfile} disabled={!!busy || !dirty}
                className="border border-ink px-5 py-2.5 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="cover-profile-save-btn">
                {busy === "save-profile" ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
              {isLive && (
                <a href={`/member/${m.id}`} target="_blank" rel="noreferrer" className="border border-ink/30 text-ink/70 px-4 py-2.5 overline flex items-center gap-2 hover:border-ink hover:text-ink transition-colors" data-testid="cover-view-live">
                  <Eye size={14} /> View live
                </a>
              )}
              <button onClick={publishNow} disabled={!!busy || !m.directory_image || !title || !body}
                className="bg-gloss-gold text-ink px-6 py-2.5 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="cover-publish-now-btn">
                {busy === "publish" ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} {isLive ? "Re-publish" : "Publish now"}
              </button>
            </div>
          </div>
          <p className="font-mono text-[10px] text-ink/40 text-right">Publish Now goes live instantly on the Cover Members grid, their individual page and the homepage rotation.</p>
        </div>
      </div>
    </section>
  );
}
